import { memo, useMemo, useState } from "react";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import { useFfnRecordStore } from "src/views/assistant/stores/FfnRecordStore";
import { useChatStreamValue } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useFfnSidebar } from "../hooks/useFfnSidebar";

const EMPTY_ARTIFACTS = {};

interface FfnSidebarProps {
  responseSources: any[];
  dialogueId: number | undefined;
}

export const FfnSidebar = memo(
  ({ responseSources, dialogueId }: FfnSidebarProps) => {
    const { isMobile } = useResponsive();
    const { rightSidebar, setRightSidebar } = useUIStore();
    const hasRecord = useFfnRecordStore((s) => s.record !== null);

    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );

    // Read from the host's store (provided by FfnChat).
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);

    const sidebarContext = useMemo(
      () => ({
        dialogueId,
        responseSources,
        artifacts,
        viewMode,
        setViewMode,
      }),
      [dialogueId, responseSources, artifacts, viewMode],
    );

    const sidebarConfig = useFfnSidebar(sidebarContext);

    const totalItems =
      (hasRecord ? 1 : 0) +
      responseSources.length +
      Object.keys(artifacts).length;

    return (
      <>
        <UnifiedSidebar
          config={sidebarConfig}
          context={sidebarContext}
          open={rightSidebar.isOpen}
          onOpenChange={(open) => setRightSidebar({ isOpen: open })}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(tab) =>
            setRightSidebar({
              selectedItem: tab,
              isArtifactItemOpen: false,
              currentArtifactId: null,
            })
          }
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />

        {isMobile && (
          <SidebarToggleButton
            open={rightSidebar.isOpen}
            onClick={() =>
              setRightSidebar({
                isOpen: !rightSidebar.isOpen,
                ...(!rightSidebar.isOpen &&
                  rightSidebar.selectedItem === null && {
                    selectedItem: hasRecord ? "ffn-record" : "sources",
                  }),
              })
            }
            badgeContent={totalItems}
            tooltipTitle={
              rightSidebar.isOpen ? "Close sidebar" : `View ${totalItems} items`
            }
            show={totalItems > 0}
          />
        )}
      </>
    );
  },
);

FfnSidebar.displayName = "FfnSidebar";

export default FfnSidebar;
